import { Component, OnInit, OnDestroy, AfterViewInit } from '@angular/core';
import { Store } from '@ngrx/store';
import { AppState } from 'src/app/store';
import { Observable, Subscription, combineLatest, of, forkJoin } from 'rxjs';
import { map, take, catchError, switchMap } from 'rxjs/operators';
import * as CartSelectors from 'src/app/store/cart/cart.selectors';
import * as CartActions from 'src/app/store/cart/cart.actions';
import * as UserProfileSelectors from 'src/app/store/user-profile/user-profile.selector';
import { StoreCart, CartItem } from 'src/app/store/cart/cart.models';
import { BusinessService } from 'src/app/services/business/business.service';
import { BusinessInformationEntity } from 'src/app/models/business.model';
import { ServiceComboEntity } from 'src/app/models/service-combo.model';
import { StaffEntity } from 'src/app/models/staff.model';
import { StaffService } from 'src/app/services/staff/staff.service';
import { UserProfile } from 'src/app/models/user-profile.model';
import {
  AppointmentService,
  CreateAppointmentRequest,
  ServiceData,
  StaffInfo,
} from 'src/app/services/appointment/appointment.service';
import { ToastService } from 'src/app/services/toast/toast.service';
import { Router } from '@angular/router';

declare var Swiper: any;

interface StoreCheckoutData {
  storeCart: StoreCart;
  storeInfo: BusinessInformationEntity | null;
  selectedDate: Date | null;
  selectedTime: string | null;
  availableTimes: string[];
  isLoadingStore: boolean;
  staffList: StaffEntity[];
  isLoadingStaff: boolean;
  staffAvailability: Map<string, boolean>; // Map of staffId to availability status
  isLoadingAvailability: boolean;
}

@Component({
  selector: 'app-checkout',
  templateUrl: './checkout.component.html',
  styleUrls: ['./checkout.component.css'],
})
export class CheckoutComponent implements OnInit, OnDestroy, AfterViewInit {
  storeCarts$: Observable<StoreCart[]>;
  storeCheckoutData: Map<string, StoreCheckoutData> = new Map();
  storeCheckoutDataArray$: Observable<StoreCheckoutData[]>;
  currentUserProfile$: Observable<UserProfile | null>;

  private subscriptions = new Subscription();
  private swiperInstances: Map<string, any> = new Map();

  isBooking = false;

  constructor(
    private store: Store<AppState>,
    private businessService: BusinessService,
    private appointmentService: AppointmentService,
    private staffService: StaffService,
    private toastService: ToastService,
    private router: Router
  ) {
    this.storeCarts$ = this.store.select(CartSelectors.selectStoreCartsArray);
    this.currentUserProfile$ = this.store.select(
      UserProfileSelectors.selectCurrentUserProfile
    );

    // Create observable that combines cart data with store information
    this.storeCheckoutDataArray$ = this.storeCarts$.pipe(
      map((storeCarts) => {
        // Remove store data that's no longer in cart
        const currentStoreIds = new Set(storeCarts.map((sc) => sc.storeId));
        this.storeCheckoutData.forEach((_, storeId) => {
          if (!currentStoreIds.has(storeId)) {
            this.storeCheckoutData.delete(storeId);
            const swiper = this.swiperInstances.get(storeId);
            if (swiper && swiper.destroy) {
              swiper.destroy();
            }
            this.swiperInstances.delete(storeId);
          }
        });

        return storeCarts.map((storeCart) => {
          let storeData = this.storeCheckoutData.get(storeCart.storeId);

          if (!storeData) {
            // Initialize new store checkout data
            storeData = {
              storeCart,
              storeInfo: null,
              selectedDate:
                storeCart.items.length > 0
                  ? this.getDateFromCartItem(storeCart.items[0])
                  : null,
              selectedTime:
                storeCart.items.length > 0
                  ? this.getTimeFromCartItem(storeCart.items[0])
                  : null,
              availableTimes: [],
              isLoadingStore: true,
              staffList: [],
              isLoadingStaff: false,
              staffAvailability: new Map(),
              isLoadingAvailability: false,
            };

            this.storeCheckoutData.set(storeCart.storeId, storeData);
            this.loadStoreInfo(storeCart.storeId);
            this.loadStaffList(storeCart.storeId);
          } else {
            // Update existing store cart data
            storeData.storeCart = storeCart;
            // Update date/time from first item if not set
            if (storeCart.items.length > 0) {
              if (!storeData.selectedDate) {
                storeData.selectedDate = this.getDateFromCartItem(
                  storeCart.items[0]
                );
              }
              if (!storeData.selectedTime) {
                storeData.selectedTime = this.getTimeFromCartItem(
                  storeCart.items[0]
                );
              }
            }
          }

          return storeData;
        });
      })
    );
  }

  ngOnInit(): void {
    // Subscribe to store checkout data to initialize swipers when stores are loaded
    this.subscriptions.add(
      this.storeCheckoutDataArray$.subscribe((storeDataArray) => {
        storeDataArray.forEach((storeData) => {
          if (storeData.storeInfo && storeData.selectedDate) {
            this.generateAvailableTimes(storeData.storeCart.storeId);
          }
        });
        // Initialize swipers after a short delay to ensure DOM is ready
        setTimeout(() => {
          this.initializeSwipers();
        }, 200);
      })
    );
  }

  ngAfterViewInit(): void {
    // Initialize swipers after view init
    setTimeout(() => {
      this.initializeSwipers();
    }, 100);
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
    // Destroy swiper instances
    this.swiperInstances.forEach((swiper) => {
      if (swiper && swiper.destroy) {
        swiper.destroy();
      }
    });
    this.swiperInstances.clear();
  }

  loadStoreInfo(storeId: string): void {
    const storeData = this.storeCheckoutData.get(storeId);
    if (!storeData || storeData.storeInfo) {
      return; // Already loaded or doesn't exist
    }

    storeData.isLoadingStore = true;
    this.subscriptions.add(
      this.businessService
        .findById(storeId)
        .pipe(
          catchError((error) => {
            console.error('Error loading store:', error);
            storeData.isLoadingStore = false;
            return of(null);
          })
        )
        .subscribe((storeInfo) => {
          storeData.storeInfo = storeInfo;
          storeData.isLoadingStore = false;
          if (storeData.selectedDate && storeInfo) {
            this.generateAvailableTimes(storeId);
          }
          // Initialize swiper after store info is loaded
          setTimeout(() => {
            this.initializeSwiperForStore(storeId);
          }, 100);
        })
    );
  }

  loadStaffList(storeId: string): void {
    const storeData = this.storeCheckoutData.get(storeId);
    if (!storeData || storeData.isLoadingStaff) {
      return; // Already loading or doesn't exist
    }

    storeData.isLoadingStaff = true;
    this.subscriptions.add(
      this.staffService
        .getStaffByStoreId(storeId)
        .pipe(
          catchError((error) => {
            console.error('Error loading staff:', error);
            storeData.isLoadingStaff = false;
            storeData.staffList = [];
            return of([]);
          })
        )
        .subscribe((staffList) => {
          // Filter to only active staff
          storeData.staffList = staffList.filter(
            (staff) => staff.status === 'active'
          );
          storeData.isLoadingStaff = false;
          
          // Check availability if date and time are already selected
          if (storeData.selectedDate && storeData.selectedTime) {
            this.checkStaffAvailability(storeId);
          }
        })
    );
  }

  checkStaffAvailability(storeId: string, itemId?: string): void {
    const storeData = this.storeCheckoutData.get(storeId);
    if (!storeData || !storeData.selectedDate || !storeData.selectedTime || storeData.staffList.length === 0) {
      return;
    }

    if (storeData.isLoadingAvailability) {
      return; // Already checking
    }

    storeData.isLoadingAvailability = true;
    
    // Only clear availability for the specific item if provided, otherwise clear all
    if (itemId) {
      // For item-specific check, we'll check all staff but only for this item's duration
    } else {
      storeData.staffAvailability.clear();
    }

    // Create datetime from selected date and time
    const [hours, minutes] = storeData.selectedTime.split(':').map(Number);
    const appointmentDateTime = new Date(storeData.selectedDate);
    appointmentDateTime.setHours(hours, minutes, 0, 0);

    // Get duration - use item's duration if checking for specific item, otherwise use max duration
    let totalDuration: number;
    if (itemId) {
      const item = storeData.storeCart.items.find(i => i.id === itemId);
      totalDuration = item?.duration || 60;
    } else {
      // Use the longest duration from all items
      totalDuration = Math.max(
        ...storeData.storeCart.items.map((item) => item.duration),
        60 // Default to 60 minutes if no items
      );
    }

    // Calculate end time
    const endDateTime = new Date(appointmentDateTime);
    endDateTime.setMinutes(endDateTime.getMinutes() + totalDuration);

    // Get staff IDs
    const staffIds = storeData.staffList.map((staff) => staff.id);

    if (staffIds.length === 0) {
      storeData.isLoadingAvailability = false;
      return;
    }

    // Check availability for each staff member
    this.subscriptions.add(
      this.appointmentService
        .getStaffAvailability(staffIds, appointmentDateTime.toISOString(), storeId)
        .pipe(
          catchError((error) => {
            console.error('Error checking staff availability:', error);
            storeData.isLoadingAvailability = false;
            // On error, mark all staff as available (fail open)
            staffIds.forEach((staffId) => {
              storeData.staffAvailability.set(staffId, true);
            });
            return of({});
          })
        )
        .subscribe((availabilityMap) => {
          // Check if each staff is available at the selected time
          staffIds.forEach((staffId) => {
            const staff = storeData.staffList.find(s => s.id === staffId);
            
            // First check if staff is working at this time (working hours check)
            const isWorking = staff ? this.isStaffWorkingAtTime(
              staff,
              storeData.selectedDate!,
              storeData.selectedTime!,
              totalDuration
            ) : false;

            if (!isWorking) {
              // Staff is not working at this time
              storeData.staffAvailability.set(staffId, false);
              return;
            }

            // Then check appointment availability
            const availableSlots = availabilityMap[staffId] || [];
            
            if (availableSlots.length === 0) {
              // No available slots means staff is busy
              storeData.staffAvailability.set(staffId, false);
              return;
            }

            // Convert available slots to Date objects and sort them
            const sortedSlots = availableSlots
              .map((slot: Date | string) => typeof slot === 'string' ? new Date(slot) : slot)
              .sort((a, b) => a.getTime() - b.getTime());

            // Check if the appointment time can fit within consecutive available slots
            // Backend returns 30-minute slots, so we need enough consecutive slots
            const slotDuration = 30 * 60 * 1000; // 30 minutes in milliseconds
            const appointmentDuration = totalDuration * 60 * 1000; // Convert minutes to milliseconds
            const slotsNeeded = Math.ceil(appointmentDuration / slotDuration);

            let isAvailable = false;

            // Find the slot that contains the appointment start time
            const startSlotIndex = sortedSlots.findIndex((slot) => {
              const slotEnd = new Date(slot.getTime() + slotDuration);
              return appointmentDateTime >= slot && appointmentDateTime < slotEnd;
            });

            if (startSlotIndex !== -1 && startSlotIndex + slotsNeeded <= sortedSlots.length) {
              // Check if we have enough consecutive slots
              let consecutive = true;
              for (let i = startSlotIndex; i < startSlotIndex + slotsNeeded - 1; i++) {
                const currentSlotEnd = new Date(sortedSlots[i].getTime() + slotDuration);
                const nextSlot = sortedSlots[i + 1];
                // Check if slots are consecutive (next slot starts when current ends)
                if (Math.abs(currentSlotEnd.getTime() - nextSlot.getTime()) > 1000) {
                  consecutive = false;
                  break;
                }
              }

              if (consecutive) {
                // Check if appointment end time fits within the last slot
                const lastSlot = sortedSlots[startSlotIndex + slotsNeeded - 1];
                const lastSlotEnd = new Date(lastSlot.getTime() + slotDuration);
                isAvailable = endDateTime <= lastSlotEnd;
              }
            }

            // Staff is available only if both working hours and appointment availability check pass
            storeData.staffAvailability.set(staffId, isAvailable);
          });

          storeData.isLoadingAvailability = false;
        })
    );
  }

  /**
   * Check if staff is working at the selected time based on their working hours
   */
  isStaffWorkingAtTime(staff: StaffEntity, date: Date, time: string, duration: number): boolean {
    if (!staff || !staff.workingHours) {
      return false;
    }

    const weekDay = this.getStaffWeekDay(date);
    const dayHours = staff.workingHours[weekDay];

    if (!dayHours || dayHours.closed) {
      return false;
    }

    // Parse time
    const [hours, minutes] = time.split(':').map(Number);
    const appointmentStart = new Date(date);
    appointmentStart.setHours(hours, minutes || 0, 0, 0);

    // Calculate appointment end time
    const appointmentEnd = new Date(appointmentStart);
    appointmentEnd.setMinutes(appointmentEnd.getMinutes() + duration);

    // Parse staff working hours
    const [openHour, openMin] = dayHours.open.split(':').map(Number);
    const [closeHour, closeMin] = dayHours.close.split(':').map(Number);

    const staffStart = new Date(date);
    staffStart.setHours(openHour, openMin || 0, 0, 0);

    const staffEnd = new Date(date);
    staffEnd.setHours(closeHour, closeMin || 0, 0, 0);

    // Check if appointment fits within staff working hours
    // Appointment must start at or after staff start time
    // Appointment must end at or before staff end time
    return appointmentStart.getTime() >= staffStart.getTime() && 
           appointmentEnd.getTime() <= staffEnd.getTime();
  }

  /**
   * Get weekday name for staff working hours (lowercase)
   */
  private getStaffWeekDay(date: Date): keyof StaffEntity['workingHours'] {
    const days: (keyof StaffEntity['workingHours'])[] = [
      'sunday',
      'monday',
      'tuesday',
      'wednesday',
      'thursday',
      'friday',
      'saturday',
    ];
    return days[date.getDay()];
  }

  getAvailableStaff(storeData: StoreCheckoutData, itemId?: string): StaffEntity[] {
    if (!storeData.selectedDate || !storeData.selectedTime) {
      // If no date/time selected, show all staff
      return storeData.staffList;
    }

    // Get item duration if checking for specific item
    let itemDuration = 60; // Default
    let selectedStaffId: string | undefined;
    if (itemId) {
      const item = storeData.storeCart.items.find(i => i.id === itemId);
      itemDuration = item?.duration || 60;
      selectedStaffId = item?.staffId;
    } else {
      // Use max duration from all items
      itemDuration = Math.max(
        ...storeData.storeCart.items.map((item) => item.duration),
        60
      );
    }

    // Filter to only available staff (both working hours and appointment availability)
    return storeData.staffList.filter((staff) => {
      // Always include currently selected staff so it shows in the dropdown
      if (selectedStaffId && staff.id === selectedStaffId) {
        return true;
      }

      // First check if staff is working at this time
      const isWorking = this.isStaffWorkingAtTime(
        staff,
        storeData.selectedDate!,
        storeData.selectedTime!,
        itemDuration
      );

      if (!isWorking) {
        return false;
      }

      // Then check appointment availability
      const isAvailable = storeData.staffAvailability.get(staff.id);
      // If availability hasn't been checked yet, show the staff (will be filtered once checked)
      return isAvailable !== false;
    });
  }

  isSelectedStaffInAvailableList(storeData: StoreCheckoutData, itemId: string): boolean {
    const item = storeData.storeCart.items.find(i => i.id === itemId);
    if (!item?.staffId) {
      return false;
    }
    const availableStaff = this.getAvailableStaff(storeData, itemId);
    return availableStaff.some(staff => staff.id === item.staffId);
  }

  getSelectedStaffForItem(storeData: StoreCheckoutData, item: CartItem): StaffEntity | undefined {
    if (!item.staffId) {
      return undefined;
    }
    return storeData.staffList.find(s => s.id === item.staffId);
  }

  isSelectedStaffNotWorking(storeData: StoreCheckoutData, item: CartItem): boolean {
    if (!item.staffId || !storeData.selectedDate || !storeData.selectedTime) {
      return false;
    }
    const staff = this.getSelectedStaffForItem(storeData, item);
    if (!staff) {
      return false;
    }
    return !this.isStaffWorkingAtTime(staff, storeData.selectedDate, storeData.selectedTime, item.duration);
  }

  isSelectedStaffBusy(storeData: StoreCheckoutData, item: CartItem): boolean {
    if (!item.staffId || !storeData.selectedDate || !storeData.selectedTime) {
      return false;
    }
    const staff = this.getSelectedStaffForItem(storeData, item);
    if (!staff) {
      return false;
    }
    const isWorking = this.isStaffWorkingAtTime(staff, storeData.selectedDate, storeData.selectedTime, item.duration);
    if (!isWorking) {
      return false; // Not busy, just not working
    }
    return storeData.staffAvailability.get(item.staffId) === false;
  }

  getUnavailableStaff(storeData: StoreCheckoutData, itemId?: string): StaffEntity[] {
    if (!storeData.selectedDate || !storeData.selectedTime) {
      return [];
    }

    // Get item duration if checking for specific item
    let itemDuration = 60; // Default
    if (itemId) {
      const item = storeData.storeCart.items.find(i => i.id === itemId);
      itemDuration = item?.duration || 60;
    } else {
      // Use max duration from all items
      itemDuration = Math.max(
        ...storeData.storeCart.items.map((item) => item.duration),
        60
      );
    }

    return storeData.staffList.filter((staff) => {
      // Check if staff is not working at this time
      const isWorking = this.isStaffWorkingAtTime(
        staff,
        storeData.selectedDate!,
        storeData.selectedTime!,
        itemDuration
      );

      if (!isWorking) {
        return true; // Staff is unavailable due to working hours
      }

      // Check appointment availability
      return storeData.staffAvailability.get(staff.id) === false;
    });
  }

  hasUnavailableStaff(storeData: StoreCheckoutData, itemId?: string): boolean {
    if (!storeData.selectedDate || !storeData.selectedTime) {
      return false;
    }

    // Get item duration if checking for specific item
    let itemDuration = 60; // Default
    if (itemId) {
      const item = storeData.storeCart.items.find(i => i.id === itemId);
      itemDuration = item?.duration || 60;
    } else {
      // Use max duration from all items
      itemDuration = Math.max(
        ...storeData.storeCart.items.map((item) => item.duration),
        60
      );
    }

    return storeData.staffList.some((staff) => {
      // Check if staff is not working at this time
      const isWorking = this.isStaffWorkingAtTime(
        staff,
        storeData.selectedDate!,
        storeData.selectedTime!,
        itemDuration
      );

      if (!isWorking) {
        return true; // Staff is unavailable due to working hours
      }

      // Check appointment availability
      return storeData.staffAvailability.get(staff.id) === false;
    });
  }

  getDateFromCartItem(item: CartItem): Date | null {
    if (!item.date) return null;
    return this.parseDate(item.date);
  }

  getTimeFromCartItem(item: CartItem): string | null {
    if (!item.startTime) return null;
    return this.getTimeString(item.startTime);
  }

  parseDate(date: Date | string): Date {
    if (date instanceof Date) {
      return date;
    }
    return new Date(date);
  }

  getTimeString(time: Date | string): string {
    const date = time instanceof Date ? time : new Date(time);
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${hours}:${minutes}`;
  }

  onDateChange(storeId: string, event: Event): void {
    const target = event.target as HTMLInputElement;
    const storeData = this.storeCheckoutData.get(storeId);
    if (!storeData) return;

    if (target.value) {
      const selectedDate = new Date(target.value);
      storeData.selectedDate = selectedDate;
      storeData.selectedTime = null; // Reset time when date changes
      storeData.staffAvailability.clear(); // Clear availability when date changes
      this.generateAvailableTimes(storeId);
      this.updateCartItemsDate(storeId, selectedDate);
    } else {
      storeData.selectedDate = null;
      storeData.selectedTime = null;
      storeData.availableTimes = [];
      storeData.staffAvailability.clear();
    }
  }

  onTimeSelect(storeId: string, time: string): void {
    const storeData = this.storeCheckoutData.get(storeId);
    if (!storeData) return;

    storeData.selectedTime = time;
    this.updateCartItemsTime(storeId, time);
    // Check staff availability when time is selected
    if (storeData.selectedDate && storeData.staffList.length > 0) {
      this.checkStaffAvailability(storeId);
    }
  }

  updateCartItemsDate(storeId: string, date: Date): void {
    const storeData = this.storeCheckoutData.get(storeId);
    if (!storeData) return;

    storeData.storeCart.items.forEach((item) => {
      const [hours, minutes] = storeData.selectedTime
        ? storeData.selectedTime.split(':').map(Number)
        : [0, 0];

      const newDateTime = new Date(date);
      newDateTime.setHours(hours, minutes, 0, 0);

      this.store.dispatch(
        CartActions.updateCartItem({
          storeId,
          itemId: item.id,
          updates: {
            date: newDateTime,
            startTime: newDateTime,
          },
        })
      );
    });
  }

  updateCartItemsTime(storeId: string, time: string): void {
    const storeData = this.storeCheckoutData.get(storeId);
    if (!storeData || !storeData.selectedDate) return;

    const [hours, minutes] = time.split(':').map(Number);
    const newDateTime = new Date(storeData.selectedDate);
    newDateTime.setHours(hours, minutes, 0, 0);

    storeData.storeCart.items.forEach((item) => {
      this.store.dispatch(
        CartActions.updateCartItem({
          storeId,
          itemId: item.id,
          updates: {
            date: storeData.selectedDate!,
            startTime: newDateTime,
          },
        })
      );
    });
  }

  onStaffChange(storeId: string, itemId: string, staffId: string): void {
    const storeData = this.storeCheckoutData.get(storeId);
    if (!storeData) return;

    // Find the selected staff
    const selectedStaff = storeData.staffList.find(
      (staff) => staff.id === staffId
    );

    if (selectedStaff) {
      const staffName = `${selectedStaff.firstName} ${selectedStaff.lastName}`.trim() || selectedStaff.nickname || 'Unknown Staff';
      
      this.store.dispatch(
        CartActions.updateCartItem({
          storeId,
          itemId,
          updates: {
            staffId: selectedStaff.id,
            staffName: staffName,
          },
        })
      );
    } else if (staffId === '' || staffId === 'unassigned') {
      // Allow unassigning staff
      this.store.dispatch(
        CartActions.updateCartItem({
          storeId,
          itemId,
          updates: {
            staffId: undefined,
            staffName: undefined,
          },
        })
      );
    }
  }

  getStaffDisplayName(staff: StaffEntity): string {
    const fullName = `${staff.firstName} ${staff.lastName}`.trim();
    return fullName || staff.nickname || 'Unknown Staff';
  }

  generateAvailableTimes(storeId: string): void {
    const storeData = this.storeCheckoutData.get(storeId);
    if (!storeData || !storeData.storeInfo || !storeData.selectedDate) {
      if (storeData) {
        storeData.availableTimes = [];
      }
      return;
    }

    const weekDay = this.getWeekDay(storeData.selectedDate);
    const dayHours = storeData.storeInfo.workingHours[weekDay];

    if (!dayHours || dayHours.closed) {
      storeData.availableTimes = [];
      return;
    }

    const today = new Date();
    const isToday = this.isSameDay(storeData.selectedDate, today);

    const times: string[] = [];
    const [openHour, openMin] = dayHours.open.split(':').map(Number);
    const [closeHour, closeMin] = dayHours.close.split(':').map(Number);

    let currentHour = isToday ? today.getHours() + 1 : openHour;
    const currentMin = isToday ? today.getMinutes() : 0;

    if (currentMin > 0) {
      currentHour += 1;
    }

    while (
      currentHour < closeHour ||
      (currentHour === closeHour && 0 < closeMin)
    ) {
      const timeStr = `${String(currentHour).padStart(2, '0')}:00`;
      times.push(timeStr);
      currentHour += 1;
    }

    storeData.availableTimes = times;
  }

  getWeekDay(date: Date): keyof BusinessInformationEntity['workingHours'] {
    const days: (keyof BusinessInformationEntity['workingHours'])[] = [
      'sunday',
      'monday',
      'tuesday',
      'wednesday',
      'thursday',
      'friday',
      'saturday',
    ];
    return days[date.getDay()];
  }

  isSameDay(date1: Date, date2: Date): boolean {
    return (
      date1.getFullYear() === date2.getFullYear() &&
      date1.getMonth() === date2.getMonth() &&
      date1.getDate() === date2.getDate()
    );
  }

  getMinDate(): string {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  formatDateForInput(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  getStoreCheckoutData(storeId: string): StoreCheckoutData | undefined {
    return this.storeCheckoutData.get(storeId);
  }

  getStoreImages(storeInfo: BusinessInformationEntity | null): string[] {
    if (!storeInfo) return [];
    const images: string[] = [];
    if (storeInfo.banner) images.push(storeInfo.banner);
    if (storeInfo.storeLogo) images.push(storeInfo.storeLogo);
    if (storeInfo.galleryPhotos && storeInfo.galleryPhotos.length > 0) {
      images.push(...storeInfo.galleryPhotos);
    }
    return images.length > 0 ? images : ['assets/img/checkout.jpg'];
  }

  initializeSwipers(): void {
    this.storeCheckoutData.forEach((_, storeId) => {
      this.initializeSwiperForStore(storeId);
    });
  }

  initializeSwiperForStore(storeId: string): void {
    const swiperId = `store-swiper-${storeId}`;
    const swiperElement = document.querySelector(`#${swiperId}`);

    if (!swiperElement) {
      return;
    }

    // Destroy existing swiper if it exists
    const existingSwiper = this.swiperInstances.get(storeId);
    if (existingSwiper && existingSwiper.destroy) {
      existingSwiper.destroy(true, true);
    }

    try {
      const swiper = new Swiper(`#${swiperId}`, {
        slidesPerView: 1,
        spaceBetween: 10,
        loop: false,
        navigation: {
          nextEl: `.swiper-button-next-${storeId}`,
          prevEl: `.swiper-button-prev-${storeId}`,
        },
        pagination: {
          el: `.swiper-pagination-${storeId}`,
          clickable: true,
        },
      });
      this.swiperInstances.set(storeId, swiper);
    } catch (error) {
      console.error('Error initializing swiper:', error);
    }
  }

  getTotalPrice(): Observable<number> {
    return this.store.select(CartSelectors.selectCartTotalPrice);
  }

  getTotalItemCount(): Observable<number> {
    return this.store.select(CartSelectors.selectCartItemCount);
  }

  removeCartItem(storeId: string, itemId: string): void {
    this.store.dispatch(CartActions.removeCartItem({ storeId, itemId }));
  }

  formatPrice(price: number): string {
    return `$${price.toFixed(2)}`;
  }

  formatDuration(duration: number): string {
    const hours = Math.floor(duration / 60);
    const minutes = duration % 60;
    if (hours > 0 && minutes > 0) {
      return `${hours}h ${minutes}m`;
    } else if (hours > 0) {
      return `${hours}h`;
    } else {
      return `${minutes}m`;
    }
  }

  async bookAppointments(): Promise<void> {
    if (this.isBooking) return; // Prevent double booking

    this.isBooking = true;

    try {
      // Get current user profile and cart data
      const [userProfile, storeCarts] = await Promise.all([
        this.currentUserProfile$.pipe(take(1)).toPromise(),
        this.storeCarts$.pipe(take(1)).toPromise(),
      ]);

      if (!userProfile) {
        alert('Please log in to book appointments');
        return;
      }

      if (!storeCarts || storeCarts.length === 0) {
        alert('Your cart is empty');
        return;
      }

      // Validate all appointments have required data
      const invalidStores = this.validateAppointmentData(storeCarts);
      if (invalidStores.length > 0) {
        // Build detailed error message
        const errorMessages: string[] = [];
        for (const storeCart of storeCarts) {
          const storeData = this.storeCheckoutData.get(storeCart.storeId);
          if (!storeData) continue;

          const storeErrors: string[] = [];
          
          if (!storeData.selectedDate || !storeData.selectedTime) {
            storeErrors.push('Date and time must be selected');
          }

          for (const item of storeCart.items) {
            if (item.staffId) {
              const isAvailable = this.isStaffAvailableForItem(storeData, item);
              if (!isAvailable) {
                const staff = storeData.staffList.find(s => s.id === item.staffId);
                const staffName = staff ? this.getStaffDisplayName(staff) : 'Selected staff';
                const isWorking = staff ? this.isStaffWorkingAtTime(
                  staff,
                  storeData.selectedDate!,
                  storeData.selectedTime!,
                  item.duration
                ) : false;
                
                if (!isWorking) {
                  storeErrors.push(`${staffName} is not working at the selected time`);
                } else {
                  storeErrors.push(`${staffName} is busy at the selected time`);
                }
              }
            }
          }

          if (storeErrors.length > 0) {
            errorMessages.push(`${storeCart.storeName || 'Store'}: ${storeErrors.join('; ')}`);
          }
        }

        this.toastService.error(
          'Cannot Book Appointment',
          errorMessages.length > 0 
            ? errorMessages.join('. ') 
            : `Please complete appointment details for: ${invalidStores.join(', ')}`,
          [
            {
              label: 'OK',
              action: () => {},
              style: 'primary',
            },
          ]
        );
        return;
      }

      // Show loading state
      console.log('Creating appointments...');

      // Create appointments for each store
      const appointmentRequests = this.createAppointmentRequests(
        storeCarts,
        userProfile
      );

      // Execute all appointment creations
      const appointments = await forkJoin(
        appointmentRequests.map((request) =>
          this.appointmentService.createAppointment(request).pipe(
            catchError((error) => {
              console.error('Error creating appointment:', error);
              throw error;
            })
          )
        )
      ).toPromise();

      console.log('Appointments created successfully:', appointments);

      // Clear cart after successful booking
      this.store.dispatch(CartActions.clearCart());

      // Show success toast with navigation options
      this.toastService.success(
        'Appointments Booked Successfully!',
        `${appointments?.length || 1} appointment${
          (appointments?.length || 1) > 1 ? 's' : ''
        } booked successfully. What would you like to do next?`,
        [
          {
            label: 'View My Appointments',
            action: () => this.router.navigate(['/appointments']),
            style: 'primary',
          },
          {
            label: 'Continue Shopping',
            action: () => this.router.navigate(['/']),
            style: 'secondary',
          },
        ]
      );
    } catch (error) {
      console.error('Error booking appointments:', error);

      // Show error toast
      this.toastService.error(
        'Booking Failed',
        'Failed to book appointments. Please try again or contact support if the problem persists.',
        [
          {
            label: 'Try Again',
            action: () => this.bookAppointments(),
            style: 'primary',
          },
          {
            label: 'Go Home',
            action: () => this.router.navigate(['/']),
            style: 'secondary',
          },
        ]
      );
    } finally {
      this.isBooking = false;
    }
  }

  private validateAppointmentData(storeCarts: StoreCart[]): string[] {
    const invalidStores: string[] = [];
    const invalidReasons: Map<string, string[]> = new Map();

    for (const storeCart of storeCarts) {
      const storeData = this.storeCheckoutData.get(storeCart.storeId);
      const reasons: string[] = [];

      if (!storeData?.selectedDate || !storeData?.selectedTime) {
        reasons.push('Date and time must be selected');
      }

      // Check if all items have required data
      for (const item of storeCart.items) {
        if (!item.date || !item.startTime) {
          reasons.push('All items must have date and time');
          break;
        }

        // Check staff availability if staff is selected
        if (item.staffId) {
          const isAvailable = this.isStaffAvailableForItem(storeData!, item);
          if (!isAvailable) {
            const staff = storeData?.staffList.find(s => s.id === item.staffId);
            const staffName = staff ? this.getStaffDisplayName(staff) : 'Selected staff';
            const isWorking = staff && storeData ? this.isStaffWorkingAtTime(
              staff,
              storeData.selectedDate!,
              storeData.selectedTime!,
              item.duration
            ) : false;
            
            if (!isWorking) {
              reasons.push(`${staffName} is not working at the selected time`);
            } else {
              reasons.push(`${staffName} is busy at the selected time`);
            }
          }
        } else {
          // If no staff is selected, check if there's at least one available staff
          const availableStaff = this.getAvailableStaff(storeData!, item.id);
          if (availableStaff.length === 0) {
            reasons.push('No available staff at the selected time. Please select a specific staff or choose a different time.');
          }
        }
      }

      if (reasons.length > 0) {
        invalidStores.push(storeCart.storeName || 'Unknown Store');
        invalidReasons.set(storeCart.storeName || 'Unknown Store', reasons);
      }
    }

    // Store reasons for display in error message
    if (invalidStores.length > 0) {
      const allReasons: string[] = [];
      Array.from(invalidReasons.values()).forEach(reasons => {
        allReasons.push(...reasons);
      });
      console.warn('Validation errors:', allReasons);
    }

    return invalidStores;
  }

  private createAppointmentRequests(
    storeCarts: StoreCart[],
    userProfile: UserProfile
  ): CreateAppointmentRequest[] {
    return storeCarts.map((storeCart) => {
      const storeData = this.storeCheckoutData.get(storeCart.storeId)!;

      // Process services and staffs for this store
      const { services, staffs, totalDuration, appointmentDateTime } =
        this.processStoreServices(storeCart, storeData);

      // Create appointment request
      const appointmentRequest: CreateAppointmentRequest = {
        userInfo: {
          displayName: userProfile.displayName,
          phoneNumber: userProfile.phoneNumber,
          photoURL: userProfile.photoURL || '',
          id: userProfile.id,
        },
        date: appointmentDateTime.toISOString(),
        duration: totalDuration,
        storeId: storeCart.storeId,
        services,
        staffs,
        isCheckedIn: false,
        isBookedOnline: true,
      };

      return appointmentRequest;
    });
  }

  private processStoreServices(
    storeCart: StoreCart,
    storeData: StoreCheckoutData
  ): {
    services: Record<string, ServiceData>;
    staffs: StaffInfo[];
    totalDuration: number;
    appointmentDateTime: Date;
  } {
    const services: Record<string, ServiceData> = {};
    const staffsMap = new Map<string, StaffInfo>();
    let totalDuration = 0;

    // Create appointment date/time
    const [hours, minutes] = storeData.selectedTime!.split(':').map(Number);
    const appointmentDateTime = new Date(storeData.selectedDate!);
    appointmentDateTime.setHours(hours, minutes, 0, 0);

    // Process each cart item
    storeCart.items.forEach((item, index) => {
      let staffId = item.staffId;
      let selectedStaff: StaffEntity | undefined;

      // If no staff is selected, randomly select an available staff
      if (!staffId || staffId === '' || staffId === 'unassigned') {
        const availableStaff = this.getAvailableStaff(storeData, item.id);
        
        if (availableStaff.length > 0) {
          // Randomly select one from available staff
          const randomIndex = Math.floor(Math.random() * availableStaff.length);
          selectedStaff = availableStaff[randomIndex];
          staffId = selectedStaff.id;
          
          // Add the randomly selected staff to staffsMap
          const staffName = this.getStaffDisplayName(selectedStaff);
        staffsMap.set(staffId, {
            id: staffId,
            displayName: staffName,
            photoURL: selectedStaff.photoURL || '',
          });
        } else {
          // If no available staff, use 'unassigned' as fallback
          staffId = 'unassigned';
        }
      } else {
        // Staff is already selected, add to staffsMap
        selectedStaff = storeData.staffList.find(s => s.id === staffId);
        if (selectedStaff && item.staffName) {
          staffsMap.set(staffId, {
            id: staffId,
          displayName: item.staffName,
            photoURL: selectedStaff.photoURL || '',
        });
        }
      }

      // Process services for this staff
      const serviceData: ServiceData = {
        combos:
          item.combos?.map((combo) => ({
            id: combo.id,
            name: combo.name,
            duration: combo.duration,
            totalPrice: combo.totalPrice,
            deductionAmount: 0, // Default values - you might want to get these from cart
            discountAmount: 0,
            taxAmount: 0,
          })) || [],
        items:
          item.items?.map((serviceItem) => ({
            id: serviceItem.id,
            name: serviceItem.name,
            duration: serviceItem.duration,
            totalPrice: serviceItem.totalPrice,
            deductionAmount: 0, // Default values - you might want to get these from cart
            discountAmount: 0,
            taxAmount: 0,
          })) || [],
        duration: item.duration,
        start: appointmentDateTime.toISOString(),
      };

      services[staffId] = serviceData;
      totalDuration += item.duration;
    });

    return {
      services,
      staffs: Array.from(staffsMap.values()),
      totalDuration,
      appointmentDateTime,
    };
  }

  canBookAppointments(): boolean {
    if (this.isBooking) return false;

    // Check if all stores have valid appointment data
    const storeCarts = this.storeCheckoutData;
    for (const [storeId, storeData] of storeCarts) {
      if (!storeData.selectedDate || !storeData.selectedTime) {
        return false;
      }

      // Check if any selected staff is unavailable, or if no staff is selected, check if there's at least one available
      for (const item of storeData.storeCart.items) {
        if (item.staffId) {
          const isAvailable = this.isStaffAvailableForItem(storeData, item);
          if (!isAvailable) {
            return false; // Cannot book if any staff is unavailable
          }
        } else {
          // If no staff is selected, check if there's at least one available staff
          const availableStaff = this.getAvailableStaff(storeData, item.id);
          if (availableStaff.length === 0) {
            return false; // Cannot book if no available staff
          }
        }
      }
    }

    return storeCarts.size > 0;
  }

  /**
   * Check if a staff is available for a specific cart item
   */
  isStaffAvailableForItem(storeData: StoreCheckoutData, item: CartItem): boolean {
    if (!item.staffId || !storeData.selectedDate || !storeData.selectedTime) {
      return true; // If no staff selected or no date/time, consider available
    }

    const staff = storeData.staffList.find(s => s.id === item.staffId);
    if (!staff) {
      return false; // Staff not found
    }

    // Check working hours
    const isWorking = this.isStaffWorkingAtTime(
      staff,
      storeData.selectedDate,
      storeData.selectedTime,
      item.duration
    );

    if (!isWorking) {
      return false; // Staff is not working at this time
    }

    // Check appointment availability
    const isAvailable = storeData.staffAvailability.get(staff.id);
    if (isAvailable === false) {
      return false; // Staff is busy
    }

    // If availability hasn't been checked yet, assume available (optimistic)
    // But we should wait for availability check to complete
    if (storeData.isLoadingAvailability) {
      return false; // Don't allow booking while checking availability
    }

    return true;
  }

  // Debug method to test toast functionality
  testToast(): void {
    console.log('Testing toast...');

    this.toastService.success(
      'Test Toast',
      'This is a test toast to debug the issue.',
      [
        {
          label: 'Test Action 1',
          action: () => console.log('Test action 1 clicked'),
          style: 'primary',
        },
        {
          label: 'Test Action 2',
          action: () => console.log('Test action 2 clicked'),
          style: 'secondary',
        },
      ]
    );
  }
}
